import React, { useState } from 'react';
import {
    Avatar,
    InfoCard,
    Link,
    OverflowTooltip,
    Progress,
    ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
    catalogApiRef,
    EntityRefLink,
    getEntityRelations,
    useEntity,
} from '@backstage/plugin-catalog-react';
import {
    DEFAULT_NAMESPACE,
    RELATION_PARENT_OF,
    stringifyEntityRef,
} from '@backstage/catalog-model';
import useAsync from 'react-use/esm/useAsync';
import Pagination from '@material-ui/lab/Pagination';
import { Box, Grid, Typography, makeStyles, createStyles } from '@material-ui/core';
import { UserEntity } from '@backstage/catalog-model';

// ---------------------------------------------------------------------------
// Helpers (inlined to avoid importing internal plugin helpers)
// ---------------------------------------------------------------------------

const getMembersFromGroups = async (
    groups: { kind: string; namespace: string; name: string }[],
    catalogApi: any,
): Promise<UserEntity[]> => {
    if (groups.length === 0) return [];
    const result = await catalogApi.getEntities({
        filter: {
            kind: 'User',
            'relations.memberof': groups.map(g =>
                stringifyEntityRef({ kind: 'group', namespace: g.namespace.toLocaleLowerCase('en-US'), name: g.name.toLocaleLowerCase('en-US') }),
            ),
        },
    });
    return result.items;
};

const getDescendantGroups = async (group: any, catalogApi: any) => {
    const seen = new Map<string, boolean>();
    const queue = [{ kind: group.kind, namespace: group.metadata.namespace ?? DEFAULT_NAMESPACE, name: group.metadata.name }];
    const result: { kind: string; namespace: string; name: string }[] = [];

    seen.set(stringifyEntityRef(queue[0]), true);

    while (queue.length > 0) {
        const current = queue.shift()!;
        const entity = await catalogApi.getEntityByRef(current);
        const children = getEntityRelations(entity, RELATION_PARENT_OF, { kind: 'Group' })
            .filter(c => !seen.has(stringifyEntityRef(c)));
        children.forEach(c => seen.set(stringifyEntityRef(c), true));
        queue.push(...children);
        result.push(...children);
    }
    return result;
};

const removeDuplicates = (entities: UserEntity[]): UserEntity[] => {
    const seen = new Map<string, boolean>();
    return entities.filter(e => {
        const ref = stringifyEntityRef(e);
        if (seen.has(ref)) return false;
        seen.set(ref, true);
        return true;
    });
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useMemberStyles = makeStyles(
    theme => createStyles({
        card: {
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[2],
            borderRadius: '4px',
            overflow: 'visible',
            position: 'relative',
            margin: theme.spacing(4, 1, 1),
            flex: '1',
            minWidth: '0px',
        },
        avatar: {
            position: 'absolute',
            top: '-2rem',
        },
    }),
    { name: 'CustomMemberComponent' },
);

const useListStyles = makeStyles(
    theme => ({
        root: { height: '100%' },
        cardContent: { overflow: 'auto' },
        memberList: {
            display: 'grid',
            gap: theme.spacing(1.5),
            gridTemplateColumns: `repeat(auto-fit, minmax(auto, ${theme.spacing(34)}px))`,
        },
    }),
    { name: 'CustomMembersListCard' },
);

// ---------------------------------------------------------------------------
// MemberComponent
// ---------------------------------------------------------------------------

const MemberComponent = ({ member }: { member: UserEntity }) => {
    const classes = useMemberStyles();
    const { metadata: { name: metaName, description }, spec: { profile } } = member;
    const displayName = profile?.displayName ?? metaName;

    return (
        <Box className={classes.card}>
            <Box display="flex" flexDirection="column" m={3} alignItems="center" justifyContent="center">
                <Avatar displayName={displayName} picture={profile?.picture} classes={classes} />
                <Box pt={2} sx={{ width: '100%' }} textAlign="center">
                    <Typography variant="h6">
                        <EntityRefLink data-testid="user-link" entityRef={member} title={displayName} />
                    </Typography>
                    {profile?.email && (
                        <Link to={`mailto:${profile.email}`}>
                            <OverflowTooltip text={profile.email} />
                        </Link>
                    )}
                    {description && (
                        <Typography variant="subtitle2">
                            <OverflowTooltip text={description} line={5} />
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

// ---------------------------------------------------------------------------
// CustomMembersListCard
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50;

export const CustomMembersListCard = () => {
    const classes = useListStyles();
    const { entity: groupEntity } = useEntity();
    const catalogApi = useApi(catalogApiRef);

    const groupName = groupEntity.metadata.name;
    const groupNamespace = groupEntity.metadata.namespace ?? DEFAULT_NAMESPACE;
    const displayName = (groupEntity.spec as any)?.profile?.displayName ?? groupName;

    const [page, setPage] = useState(1);

    // Fetch direct members
    const { loading, error, value: directMembers } = useAsync(async () => {
        const result = await catalogApi.getEntities({
            filter: {
                kind: 'User',
                'relations.memberof': [
                    stringifyEntityRef({
                        kind: 'group',
                        namespace: groupNamespace.toLocaleLowerCase('en-US'),
                        name: groupName.toLocaleLowerCase('en-US'),
                    }),
                ],
            },
        });
        return result.items as UserEntity[];
    }, [catalogApi, groupEntity]);

    // Fetch descendant members (aggregated — always on)
    const { loading: loadingDescendants, value: descendantMembers } = useAsync(async () => {
        const descendants = await getDescendantGroups(groupEntity, catalogApi);
        return getMembersFromGroups(descendants, catalogApi);
    }, [catalogApi, groupEntity]);

    if (loading || loadingDescendants) return <Progress />;
    if (error) return <ResponseErrorPanel error={error} />;

    const members = removeDuplicates(
        [...(directMembers ?? []), ...(descendantMembers ?? [])]
            .sort((a, b) => stringifyEntityRef(a).localeCompare(stringifyEntityRef(b))),
    );

    const nbPages = Math.ceil(members.length / PAGE_SIZE);
    const paginationSuffix = nbPages >= 2 ? ` · ${page}/${nbPages}` : '';

    return (
        <Grid item className={classes.root}>
            <InfoCard
                title={`Membros (${members.length}${paginationSuffix})`}
                subheader={`Membros de ${displayName}`}
                className={classes.root}
                cardClassName={classes.cardContent}
                {...(nbPages > 1 ? {
                    actions: (
                        <Pagination
                            count={nbPages}
                            page={page}
                            onChange={(_, p) => setPage(p)}
                            showFirstButton
                            showLastButton
                        />
                    ),
                } : {})}
            >
                {members.length > 0 ? (
                    <Box className={classes.memberList}>
                        {members
                            .slice(PAGE_SIZE * (page - 1), PAGE_SIZE * page)
                            .map(member => (
                                <MemberComponent key={stringifyEntityRef(member)} member={member} />
                            ))}
                    </Box>
                ) : (
                    <Box p={2}>
                        <Typography>Nenhum membro encontrado.</Typography>
                    </Box>
                )}
            </InfoCard>
        </Grid>
    );
};
